import { useEffect, useState } from "react";
import { api } from "rsa/api";
import { payForRequest } from "rsa/razorpay";
import { useSocket } from "rsa/useSocket";
import MapView from "rsa/MapView";

const FLOW = [
  ["broadcasting", "Alerting nearby vendors", "Your request is broadcasting to collision-care partners"],
  ["claimed", "Vendor accepted", "A partner has taken your request"],
  ["enroute", "On the way", "The vendor is heading to your location"],
  ["arrived", "Arrived", "The vendor has reached you"],
  ["in_progress", "Work in progress", "Assistance is underway"],
  ["completed", "Completed", "Job done — payment settled"],
];
const rank = (s) => { const i = FLOW.findIndex((f) => f[0] === s); return i < 0 ? (s === "created" ? 0 : -1) : i; };

export default function RequestTracker({ requestId, token, pickup }) {
  const [req, setReq] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [payment, setPayment] = useState(null);
  const [paying, setPaying] = useState(false);
  const [ambulance, setAmbulance] = useState(null);
  const [insurance, setInsurance] = useState(null);

  const refresh = () => api("GET", `/rsa/requests/${requestId}`, { token }).then(async (r) => {
    setReq(r);
    if (r.status === "completed") api("GET", `/rsa/requests/${requestId}/payment`, { token }).then((p) => p && setPayment((prev) => prev || p)).catch(() => {});
  }).catch(() => {});
  useEffect(() => { refresh(); }, [requestId]); // eslint-disable-line

  useSocket(token, {
    "request:update": (p) => {
      if (p.vendor) setVendor(p.vendor);
      if (p.estimate) setEstimate(p.estimate);
      if (p.payment) setPayment(p.payment);
      refresh();
    },
    "payment:update": (p) => setPayment((prev) => (prev ? { ...prev, status: p.status } : prev)),
    "ambulance:update": (p) => setAmbulance(p),
    "insurance:update": (p) => setInsurance(p),
  }, requestId);

  const pay = async () => {
    setPaying(true);
    try {
      const verified = await payForRequest(payment, token, { name: req?.guest_name, phone: req?.guest_phone });
      setPayment((prev) => ({ ...prev, status: verified.status || "captured" }));
    } catch (e) { /* cancelled / failed — leave button */ } finally { setPaying(false); }
  };

  const status = req?.status || "broadcasting";
  const cur = rank(status);
  const cancelled = status === "cancelled" || status === "expired";
  const v = vendor || (req?.vendor_name ? { name: req.vendor_name, phone: req.vendor_phone } : null);
  const vendorMarker = req?.vendor_lat ? [{
    id: req.assigned_vendor_id, latitude: req.vendor_lat, longitude: req.vendor_lng,
    business_name: req.vendor_name, distance_km: req.estimated_distance_km,
  }] : [];

  return (
    <div className="avah-rsa__card">
      <div className="avah-rsa__row" style={{ justifyContent: "space-between" }}>
        <b style={{ fontSize: 15 }}>{req?.request_ref || "Request"}</b>
        <span className={`avah-rsa__pill ${cancelled ? "" : "avah-rsa__pill--live"}`}>
          {cancelled ? status : "● live"}</span>
      </div>

      <div style={{ margin: "12px 0" }}>
        <MapView pickup={pickup} vendors={vendorMarker} highlightVendorId={req?.assigned_vendor_id} height={200} />
      </div>

      {v && (
        <div className="avah-rsa__vehicle">
          🔧 <b>{v.name}</b> is assisting you{estimate?.etaMinutes ? ` · ETA ~${estimate.etaMinutes} min` : ""}
          {v.phone && <> · <a href={`tel:${v.phone}`}>{v.phone}</a></>}
          {(estimate?.estimatedFee || req?.estimated_fee) &&
            <div style={{ marginTop: 4 }}>Estimated fee: <b>₹{estimate?.estimatedFee || Math.round(req.estimated_fee)}</b> (cashless)</div>}
        </div>
      )}

      {cancelled ? (
        <p className="avah-rsa__err" style={{ marginTop: 14 }}>
          {status === "expired" ? "No vendor was available nearby. Please try again or call 112." : "Request cancelled."}
        </p>
      ) : (
        <ul className="avah-rsa__timeline">
          {FLOW.map(([key, title, sub], i) => {
            const state = i < cur ? "done" : i === cur ? "active" : "";
            return (
              <li key={key} className={`avah-rsa__step ${state ? "avah-rsa__step--" + state : ""}`}>
                <span className="avah-rsa__step__dot">{i < cur ? "✓" : i + 1}</span>
                <div><b>{title}</b><span>{sub}</span></div>
              </li>
            );
          })}
        </ul>
      )}

      {ambulance && (
        <div className="avah-rsa__pill avah-rsa__pill--ok" style={{ marginTop: 8 }}>
          🚑 Ambulance {ambulance.status}{ambulance.eta_minutes ? ` · ETA ${ambulance.eta_minutes} min` : ""}
        </div>
      )}
      {insurance && (
        <div className="avah-rsa__pill avah-rsa__pill--ok" style={{ marginTop: 8 }}>
          🛡️ Insurer notified ({insurance.insurer})
        </div>
      )}
      {payment && payment.status === "captured" && (
        <div className="avah-rsa__pill avah-rsa__pill--ok" style={{ marginTop: 8 }}>
          💳 Paid ₹{Math.round(payment.amount)} · cashless
        </div>
      )}
      {payment && payment.status !== "captured" && (
        <button className="avah-rsa__btn avah-rsa__btn--primary" style={{ marginTop: 12 }} disabled={paying} onClick={pay}>
          {paying ? "Processing…" : `💳 Pay ₹${Math.round(payment.amount)} (cashless)`}
        </button>
      )}
    </div>
  );
}
